import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ContainerIdentifierWithIndex } from '®shared/container-structure';
import { PersistSuccess } from '®shared/container-structure/state/container-persist-edit.actions';
import { ListPublishAction } from '®shared/reading-list/models';
import { MetaData, TarlV3Jsonapi } from '®shared/tarl-v3-jsonapi';
import { convertToJsonapiResponseDatum } from '®shared/tarl-v3-jsonapi/jsonapi-utils';
import { Tag } from '®shared/tenant/tag';

import { InvalidListEditResponseException } from './invalid-list-edit-response-exception';

export interface ListEtagMeta extends MetaData<any> {
  meta: {
    list_id: string;
    list_etag: string;
  };
}

@Injectable()
export class ReadingListEditService {
  constructor(private tarlV3Jsonapi: TarlV3Jsonapi) {}

  convertToPersistSuccess(response: ListEtagMeta): PersistSuccess {
    if (response && response.meta && response.meta.list_etag) {
      return { etag: response.meta.list_etag };
    }
    throw new Error(`No etag found in response: ${JSON.stringify(response)}`);
  }

  throwInvalidListEditResponseException(error: HttpErrorResponse) {
    return observableThrowError(new InvalidListEditResponseException(error));
  }

  /**
   * Create a publish list action resource in RL that should eventually result
   * in a published list. The publish workflow is asynchronous and so we should
   * expect a 202 response with the current status and a publish action ID,
   * and a review URL if appropriate
   *
   * @param {string} listId - the ID of the list being published
   * @param {string} listETag - the current ETag of the list
   * @returns {Observable<ListPublishAction>}
   */
  publishList(listId: string, listETag: string): Observable<ListPublishAction> {
    const path = this.tarlV3Jsonapi.path(
      '/draft_lists/{listId}/publish_actions',
      {
        listId,
      },
    );

    const createPublishAction = {
      data: {
        id: undefined,
        type: 'list_publish_actions',
      },
      meta: {
        list_etag: listETag,
      },
    };

    return this.tarlV3Jsonapi
      .postRawJsonapiResponse(path, createPublishAction)
      .pipe(
        map(response =>
          convertToJsonapiResponseDatum<ListPublishAction>(response),
        ),
      );
  }

  /**
   * Fetch a publish list action resource from RL. This call can be polled
   * to get updates on the status of publishing. When it has succeeded, the
   * list ETag is updated.
   *
   * @param {string} listId - the ID of the list being published
   * @param {string} publishActionId - the ID of the publish action to fetch
   * @returns {Observable<ListPublishAction>}
   */
  getListPublishAction(
    listId: string,
    publishActionId: string,
  ): Observable<ListPublishAction> {
    const path = this.tarlV3Jsonapi.path(
      '/draft_lists/{listId}/publish_actions/{publishActionId}',
      {
        listId,
        publishActionId,
      },
    );

    return this.tarlV3Jsonapi.getResource<ListPublishAction>(path);
  }

  /**
   * Create a resource item on a list using the API
   *
   * @param {string} listId - the ID of the list the item is being created in
   * @param {string} eTag - the current ETag of the list
   * @param {string} itemId - the ID of the item being created
   * @param {string} resourceId - the ID of the resource this item is linked to
   * @param {ContainerIdentifierWithIndex} targetContainer - the container and position we are creating the item within
   * @return {Observable<PersistSuccess>}
   */
  createResourceItem(
    listId: string,
    eTag: string,
    itemId: string,
    resourceId: string,
    targetContainer: ContainerIdentifierWithIndex,
    importanceId?: string,
    libraryNote?: string,
    studentNote?: string,
    tags?: Tag[],
  ): Observable<PersistSuccess> {
    const path = this.tarlV3Jsonapi.path('/draft_items', {});

    const createResourceItem = {
      data: {
        id: itemId,
        type: 'items',
        attributes: {},
        relationships: {
          container: {
            data: {
              id: targetContainer.id,
              type: targetContainer.type,
            },
            meta: {
              index: targetContainer.index,
            },
          },
          resource: {
            data: {
              id: resourceId,
              type: 'resources',
            },
          },
        },
      },
      meta: {
        list_id: listId,
        list_etag: eTag,
      },
    };

    if (libraryNote) {
      createResourceItem.data.attributes['library_note'] = libraryNote;
    }

    if (studentNote) {
      createResourceItem.data.attributes['student_note'] = studentNote;
    }

    if (importanceId) {
      createResourceItem.data.relationships['importance'] = {
        data: {
          id: importanceId,
          type: 'importances',
        },
      };
    }

    if (tags && tags.length > 0) {
      const tagsIdArray = tags.map(tag => ({
        type: 'tags',
        id: tag.id,
      }));

      createResourceItem.data.relationships['tags'] = {
        data: tagsIdArray,
      };
    }

    return this.tarlV3Jsonapi
      .postRawJsonapiResponse(path, createResourceItem)
      .pipe(
        map(this.convertToPersistSuccess),
        catchError(this.throwInvalidListEditResponseException),
      );
  }

  /**
   * Create a note item on a list using the API
   *
   * @param {string} listId - the ID of the list the item is being created in
   * @param {string} eTag - the current ETag of the list
   * @param {string} itemId - the ID of the item being created
   * @param {string} studentNote - the note that is part of the item being created
   * @param {ContainerIdentifierWithIndex} targetContainer - the container and position we are creating the item within
   * @return {Observable<PersistSuccess>}
   */
  createNoteItem(
    listId: string,
    eTag: string,
    itemId: string,
    studentNote: string,
    targetContainer: ContainerIdentifierWithIndex,
  ): Observable<PersistSuccess> {
    const path = this.tarlV3Jsonapi.path('/draft_items', {});

    const createNoteItem = {
      data: {
        id: itemId,
        type: 'items',
        attributes: {
          student_note: studentNote,
        },
        relationships: {
          container: {
            data: {
              id: targetContainer.id,
              type: targetContainer.type,
            },
            meta: {
              index: targetContainer.index,
            },
          },
        },
      },
      meta: {
        list_id: listId,
        list_etag: eTag,
      },
    };

    return this.tarlV3Jsonapi
      .postRawJsonapiResponse(path, createNoteItem)
      .pipe(
        map(this.convertToPersistSuccess),
        catchError(this.throwInvalidListEditResponseException),
      );
  }

  /**
   * Update the student note on an item on a list using the API
   * This method is used to update notes on resource items, as well as note only
   * items
   *
   * @param {string} listId - the ID of the list the item belongs to
   * @param {string} eTag - the current ETag of the list
   * @param {string} itemId - the ID of the item being updated
   * @param {string|null} studentNote - the student note to set, or null to remove an existing one
   * @return {Observable<PersistSuccess>}
   */
  updateItemStudentNote(
    listId: string,
    eTag: string,
    itemId: string,
    studentNote: string | null,
  ): Observable<PersistSuccess> {
    const path = this.tarlV3Jsonapi.path('/draft_items/{itemId}', { itemId });

    const updateStudentNote = {
      data: {
        id: itemId,
        type: 'items',
        attributes: {
          student_note: studentNote,
        },
      },
      meta: {
        list_id: listId,
        list_etag: eTag,
      },
    };

    return this.tarlV3Jsonapi
      .patchRawJsonapiResponse(path, updateStudentNote)
      .pipe(
        map(this.convertToPersistSuccess),
        catchError(this.throwInvalidListEditResponseException),
      );
  }

  /**
   * Update the library note on an item on a list using the API
   * This method is used to update library notes on resource items
   *
   * @param {string} listId - the ID of the list the item belongs to
   * @param {string} eTag - the current ETag of the list
   * @param {string} itemId - the ID of the item being updated
   * @param {string|null} libraryNote - the library note to set, or null to remove an existing one
   * @return {Observable<PersistSuccess>}
   */
  updateItemLibraryNote(
    listId: string,
    eTag: string,
    itemId: string,
    libraryNote: string | null,
  ): Observable<PersistSuccess> {
    const path = this.tarlV3Jsonapi.path('/draft_items/{itemId}', { itemId });

    const updateLibraryNote = {
      data: {
        id: itemId,
        type: 'items',
        attributes: {
          library_note: libraryNote,
        },
      },
      meta: {
        list_id: listId,
        list_etag: eTag,
      },
    };

    return this.tarlV3Jsonapi
      .patchRawJsonapiResponse(path, updateLibraryNote)
      .pipe(
        map(this.convertToPersistSuccess),
        catchError(this.throwInvalidListEditResponseException),
      );
  }

  /**
   * Update the importance of an item on a list using the API
   *
   * @param {string} listId - the ID of the list the item belongs to
   * @param {string} eTag - the current ETag of the list
   * @param {string} itemId - the ID of the item being updated
   * @param {string|null} importanceId - the ID of the importance to set, or null to remove an existing one
   * @return {Observable<PersistSuccess>}
   */
  updateItemImportance(
    listId: string,
    eTag: string,
    itemId: string,
    importanceId: string | null,
  ): Observable<PersistSuccess> {
    const path = this.tarlV3Jsonapi.path('/draft_items/{itemId}', { itemId });

    const importance =
      importanceId === null ? null : { id: importanceId, type: 'importances' };

    const updateImportance = {
      data: {
        id: itemId,
        type: 'items',
        relationships: {
          importance: {
            data: importance,
          },
        },
      },
      meta: {
        list_id: listId,
        list_etag: eTag,
      },
    };

    return this.tarlV3Jsonapi
      .patchRawJsonapiResponse(path, updateImportance)
      .pipe(
        map(this.convertToPersistSuccess),
        catchError(this.throwInvalidListEditResponseException),
      );
  }

  /**
   * Move an item on a list using the API
   *
   * @param {string} listId - the ID of the list the item being moved is on
   * @param {string} etag - the current etag of the list
   * @param {string} itemId - the ID of the item being moved
   * @param {ContainerIdentifierWithIndex} targetContainer - the container and position we are moving the item into
   * @return {Observable<PersistSuccess>} - the
   */
  moveListItem(
    listId: string,
    etag: string,
    itemId: string,
    targetContainer: ContainerIdentifierWithIndex,
  ): Observable<PersistSuccess> {
    const path = this.tarlV3Jsonapi.path(
      '/items/{itemId}/relationships/container',
      {
        itemId,
      },
    );

    const moveItemContainer = {
      data: {
        id: targetContainer.id,
        type: targetContainer.type,
        meta: { index: targetContainer.index },
      },
      meta: {
        list_id: listId,
        list_etag: etag,
      },
    };

    return this.tarlV3Jsonapi
      .patchRawJsonapiResponse(path, moveItemContainer)
      .pipe(
        map(this.convertToPersistSuccess),
        catchError(this.throwInvalidListEditResponseException),
      );
  }

  /**
   * Move a section on a list using the API
   *
   * @param {string} listId - the ID of the list the section being moved is on
   * @param {string} etag - the current etag of the list
   * @param {string} sectionId - the ID of the section being moved
   * @param {ContainerIdentifierWithIndex} targetContainer - the container and position we are moving the section into
   * @return {Observable<PersistSuccess>}
   */
  moveSection(
    listId: string,
    etag: string,
    sectionId: string,
    targetContainer: ContainerIdentifierWithIndex,
  ): Observable<PersistSuccess> {
    const path = this.tarlV3Jsonapi.path(
      '/sections/{sectionId}/relationships/container',
      {
        sectionId,
      },
    );

    const moveSectionContainer = {
      data: {
        id: targetContainer.id,
        type: targetContainer.type,
        meta: { index: targetContainer.index },
      },
      meta: {
        list_id: listId,
        list_etag: etag,
      },
    };

    return this.tarlV3Jsonapi
      .patchRawJsonapiResponse(path, moveSectionContainer)
      .pipe(
        map(this.convertToPersistSuccess),
        catchError(this.throwInvalidListEditResponseException),
      );
  }

  /**
   * Create a section on a list using the API
   *
   * @param {string} listId - the ID of the list the section is being created in
   * @param {string} etag - the current etag of the list
   * @param {string} sectionId - the ID of the section being created
   * @param {string} sectionTitle - the title of the section being created
   * @param {string} sectionDescription - the description of the section being created
   * @param {ContainerIdentifierWithIndex} targetContainer - the container and position we are creating the section within
   * @return {Observable<PersistSuccess>}
   */
  createSection(
    listId: string,
    etag: string,
    sectionId: string,
    sectionTitle: string,
    sectionDescription: string | null,
    targetContainer: ContainerIdentifierWithIndex,
  ): Observable<PersistSuccess> {
    const path = this.tarlV3Jsonapi.path('/draft_sections', {});

    const createSection = {
      data: {
        id: sectionId,
        type: 'sections',
        attributes: {
          title: sectionTitle,
        },
        relationships: {
          container: {
            data: {
              id: targetContainer.id,
              type: targetContainer.type,
            },
            meta: {
              index: targetContainer.index,
            },
          },
          list: {
            data: {
              id: listId,
              type: 'lists',
            },
          },
        },
      },
      meta: {
        list_id: listId,
        list_etag: etag,
      },
    };

    if (sectionDescription) {
      createSection.data.attributes['description'] = sectionDescription;
    }

    return this.tarlV3Jsonapi
      .postRawJsonapiResponse(path, createSection)
      .pipe(
        map(this.convertToPersistSuccess),
        catchError(this.throwInvalidListEditResponseException),
      );
  }

  /**
   * Updates a section on a list using the API
   *
   * @param {string} listId - the ID of the list the section being updated is in
   * @param {string} etag - the current etag of the list
   * @param {string} sectionId - the ID of the section being updated
   * @param {string} sectionTitle - the title of the section being updated
   * @param {string} sectionDescription - the description of the section being updated
   * @return {Observable<PersistSuccess>}
   */
  updateSection(
    listId: string,
    etag: string,
    sectionId: string,
    sectionTitle: string | null,
    sectionDescription: string | null,
  ): Observable<PersistSuccess> {
    const path = this.tarlV3Jsonapi.path('/draft_sections/{sectionId}', {
      sectionId,
    });

    const updateSection = {
      data: {
        id: sectionId,
        type: 'sections',
        attributes: {
          title: sectionTitle,
          description: sectionDescription,
        },
      },
      meta: {
        list_id: listId,
        list_etag: etag,
      },
    };

    return this.tarlV3Jsonapi
      .patchRawJsonapiResponse(path, updateSection)
      .pipe(
        map(this.convertToPersistSuccess),
        catchError(this.throwInvalidListEditResponseException),
      );
  }

  /**
   * Deletes a section on a list using the API
   *
   * @param {string} listId - the ID of the list the section being deleted is on
   * @param {string} etag - the current etag of the list
   * @param {string} sectionId - the ID of the section being deleted
   * @return {Observable<PersistSuccess>}
   */
  deleteSection(
    listId: string,
    etag: string,
    sectionId: string,
  ): Observable<PersistSuccess> {
    const path = this.tarlV3Jsonapi.path('/draft_sections/{sectionId}', {
      sectionId,
    });

    const deleteSectionMeta = {
      meta: {
        list_id: listId,
        list_etag: etag,
      },
    };

    return this.tarlV3Jsonapi
      .deleteRawJsonapiResponse<ListEtagMeta>(path, deleteSectionMeta)
      .pipe(
        map(this.convertToPersistSuccess),
        catchError(this.throwInvalidListEditResponseException),
      );
  }

  /**
   * Deletes an item on a list using the API
   *
   * @param {string} listId - the ID of the list the item being deleted is on
   * @param {string} etag - the current etag of the list
   * @param {string} itemId - the ID of the item being deleted
   * @return {Observable<PersistSuccess>}
   */
  deleteItem(
    listId: string,
    etag: string,
    itemId: string,
  ): Observable<PersistSuccess> {
    const path = this.tarlV3Jsonapi.path('/draft_items/{itemId}', { itemId });

    const deleteItemMeta = {
      meta: {
        list_id: listId,
        list_etag: etag,
      },
    };

    return this.tarlV3Jsonapi
      .deleteRawJsonapiResponse<ListEtagMeta>(path, deleteItemMeta)
      .pipe(
        map(this.convertToPersistSuccess),
        catchError(this.throwInvalidListEditResponseException),
      );
  }

  /**
   * Update the tags of an item on a list using the API
   *
   * @param {string} listId - the ID of the list the item belongs to
   * @param {string} eTag - the current ETag of the list
   * @param {string} itemId - the ID of the item being updated
   * @param {Tag[]} tags - the tags to set against the list item
   * @return {Observable<PersistSuccess>}
   */
  updateItemTags(
    listId: string,
    eTag: string,
    itemId: string,
    tags: Tag[],
  ): Observable<PersistSuccess> {
    const path = this.tarlV3Jsonapi.path('/draft_items/{itemId}', { itemId });

    const tagsIdArray = tags.map(tag => ({
      type: 'tags',
      id: tag.id,
    }));

    const updateItemTags = {
      data: {
        id: itemId,
        type: 'items',
        relationships: {
          tags: {
            data: tagsIdArray,
          },
        },
      },
      meta: {
        list_id: listId,
        list_etag: eTag,
      },
    };

    return this.tarlV3Jsonapi
      .patchRawJsonapiResponse(path, updateItemTags)
      .pipe(
        map(this.convertToPersistSuccess),
        catchError(this.throwInvalidListEditResponseException),
      );
  }
}
